<template>
  <section class="container-fluid mt-5 py-5">
    <div class="container mt-5 px-3">
      <div class="row align-items-center mb-5">
        <div class="col-md-6 mb-3 mb-md-0">
          <h2 class="mb-1 d-flex align-items-center text-light fs-1">
            Detalles del Pedido<span class="text-primary">.</span>
          </h2>
        </div>
        <div class="col-md-6 d-lg-flex justify-content-lg-end">
          <router-link
            to="/admin/pedidos"
            class="btn btn-outline-primary btn-lg"
          >
            <i class="fas fa-arrow-left me-2"></i>
            Volver a la lista de pedidos
          </router-link>
        </div>
      </div>

      <div v-if="isLoading" class="row g-4">
        <div class="col-12">
          <div class="skeleton-loader">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-75"></div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button
          @click="fetchOrderDetails"
          class="btn btn-outline-danger btn-sm ms-3"
        >
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <div v-else class="row g-4">
        <div class="col-12">
          <div class="card bg-dark text-light">
            <div class="card-body">
              <div class="row">
                <!-- Información del cliente -->
                <div class="col-md-6 mb-4">
                  <h5 class="card-title fs-5 fw-semibold mb-3">Información del Cliente</h5>
                  <div class="customer-info">
                    <p><span class="fw-semibold">Nombre:</span> <br>{{ order.recipient_name }}</p>
                    <p><span class="fw-semibold">Teléfono:</span> <br>{{ order.customer_phone }}</p>
                    <p><span class="fw-semibold">Email:</span> <br>{{ order.customer_email }}</p>
                    <p>
                      <span class="fw-semibold">Estado:</span><br>
                      <span :class="statusClass">{{ order.status_display }}</span>
                    </p>
                    <p>
                      <span class="fw-semibold">Tipo de entrega:</span><br>
                      {{ order.delivery_type_display }}
                    </p>
                    <p>
                      <span class="fw-semibold">Dirección:</span><br>
                      {{ order.shipping_address }}
                    </p>
                    <p>
                      <span class="fw-semibold">Fecha de Creación:</span><br>
                      {{ parseTimestamp(order.created_at) }}
                    </p>
                  </div>
                </div>
                
                <!-- Productos -->
                <div class="col-md-6">
                  <h5 class="card-title fs-5 fw-semibold mb-3">Productos</h5>
                  
                  <!-- Mensaje cuando no hay productos -->
                  <div v-if="!orderProducts || orderProducts.length === 0" class="alert alert-warning">
                    No se encontraron productos en este pedido.
                  </div>
                  
                  <!-- Lista de productos -->
                  <div v-else class="product-list">
                    <div v-for="product in orderProducts" :key="product.id" class="product-item mb-3">
                      <div class="d-flex align-items-center">
                        <div class="order-product-image">
                          <img
                            :src="product.product_image || '/logo.png'"
                            :alt="product.product_name || 'Producto'"
                            class="img-thumbnail"
                          />
                        </div>
                        <div class="product-details ms-3">
                          <h6 class="product-name mb-1">{{ product.product_name }}</h6>
                          <div class="product-price">${{ formatPriceString(product.price_at_time) }}</div>
                          <div class="product-quantity text-muted">
                            Cantidad: {{ product.quantity }}
                          </div>
                          <div v-if="product.blister_info" class="blister-info mt-1 text-muted">
                            <small>{{ product.blister_info.seed_count }} semillas</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Total del pedido -->
                  <div class="order-total mt-4">
                    <div class="d-flex justify-content-between align-items-center">
                      <h5 class="mb-0">Total:</h5>
                      <div class="total-price">${{ formatPriceString(order.total) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Botones de acción -->
              <div class="mt-4 d-flex justify-content-end gap-2">
                <button class="btn btn-secondary" @click="editOrder">
                  <i class="fas fa-edit me-1"></i>
                  Editar Pedido
                </button>
                <button class="btn btn-danger" @click="deleteOrder">
                  <i class="fas fa-trash me-1"></i>
                  Eliminar Pedido
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Edit Order Modal -->
  <div
    class="modal fade"
    id="editOrderModal"
    tabindex="-1"
    aria-labelledby="editOrderModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content bg-dark text-light">
        <div class="modal-header">
          <h5 class="modal-title" id="editOrderModalLabel">Editar Pedido</h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedOrder">
            <div class="mb-3">
              <label for="customerName" class="form-label"
                >Nombre del Cliente</label
              >
              <input
                type="text"
                class="form-control"
                id="customerName"
                v-model="selectedOrder.customer_name"
              />
            </div>
            <div class="mb-3">
              <label for="customerPhone" class="form-label"
                >Teléfono del Cliente</label
              >
              <input
                type="text"
                class="form-control"
                id="customerPhone"
                v-model="selectedOrder.customer_phone"
              />
            </div>
            <div class="mb-3">
              <label for="orderStatus" class="form-label">Estado</label>
              <select
                class="form-control"
                id="orderStatus"
                v-model="selectedOrder.status"
              >
                <option value="pending">Pendiente</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
          </div>
          <div v-else>
            <p>No se encontraron detalles del pedido.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cerrar
          </button>
          <button type="button" class="btn btn-primary" @click="updateOrder">
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import {
  getOrder,
  updateOrder as updateOrderAPI,
  deleteOrder as deleteOrderAPI,
} from "@/services/orders";
import { getProduct } from "@/services/products";
import { parseTimestamp } from "@/utils/dates";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const toast = useToast();
const route = useRoute();
const router = useRouter();
const orderId = route.params.orderId;
const orderProducts = ref([]);

const order = ref(null);
const isLoading = ref(true);
const error = ref(null);
const selectedOrder = ref(null);
const bootstrap = window.bootstrap;

const fetchOrderDetails = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await getOrder(orderId);
    order.value = response;
    
    // Usar directamente order_products de la respuesta
    if (order.value && order.value.order_products && order.value.order_products.length > 0) {
      console.log("Productos encontrados:", order.value.order_products);
      orderProducts.value = order.value.order_products;
    } else if (order.value && order.value.products) {
      // Fallback al método anterior si existe products
      orderProducts.value = await Promise.all(
        order.value.products.map(async (productId) => {
          const product = await getProduct(productId);
          return product;
        })
      );
    } else {
      // Si no hay productos, inicializar como array vacío
      orderProducts.value = [];
      console.warn("No se encontraron productos en el pedido");
    }
  } catch (err) {
    error.value = err.message;
    console.error("Error fetching order details:", err);
    toast.error("Error al cargar los detalles del pedido.");
  } finally {
    isLoading.value = false;
  }
};

const editOrder = () => {
  selectedOrder.value = { ...order.value };
  const editOrderModal = new bootstrap.Modal(
    document.getElementById("editOrderModal")
  );
  editOrderModal.show();
};

const updateOrder = async () => {
  try {
    await updateOrderAPI(selectedOrder.value.id, selectedOrder.value);
    fetchOrderDetails();
    const editOrderModal = bootstrap.Modal.getInstance(
      document.getElementById("editOrderModal")
    );
    editOrderModal.hide();
    toast.success("Pedido actualizado correctamente.");
  } catch (err) {
    console.error("Error updating order:", err);
    toast.error("Error al actualizar el pedido.");
  }
};

const deleteOrder = async () => {
  try {
    await deleteOrderAPI(order.value.id);
    router.push("/admin/pedidos");
    toast.success("Pedido eliminado correctamente.");
  } catch (err) {
    console.error("Error deleting order:", err);
    toast.error("Error al eliminar el pedido.");
  }
};


// Nueva función para manejar precios como string
const formatPriceString = (price) => {
  if (price === undefined || price === null) {
    return "0";
  }
  
  // Convertir a número si es string
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  // Verificar si es un número válido después de la conversión
  if (isNaN(numericPrice)) {
    return "0";
  }
  
  return numericPrice.toLocaleString("es-CL");
};

const statusClass = computed(() => {
  if (!order.value) return {};
  
  return {
    "badge bg-warning": order.value.status === "pending",
    "badge bg-success": order.value.status === "completed",
    "badge bg-danger": order.value.status === "cancelled",
  };
});

// Lifecycle
onMounted(() => {
  fetchOrderDetails();
});
</script>

<style scoped>
.card {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease-in-out;
}

.card:hover {
  transform: translateY(-0.25rem);
}

.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.skeleton-text {
  height: 20px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.product-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.product-item {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s ease;
}

.product-item:hover {
  background: rgba(0, 0, 0, 0.3);
}

.order-product-image {
  width: 80px;
  height: 80px;
  min-width: 80px;
  overflow: hidden;
  border-radius: 6px;
}

.order-product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-name {
  font-weight: 600;
  color: var(--white-color);
}

.product-price {
  font-weight: 600;
  color: var(--primary-color);
}

.customer-info p {
  margin-bottom: 1rem;
}

.total-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
}

.order-total {
  background: rgba(0, 0, 0, 0.2);
  padding: 15px;
  border-radius: 8px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 767px) {
  .product-list {
    max-height: 300px;
  }
  
  .order-product-image {
    width: 60px;
    height: 60px;
    min-width: 60px;
  }
}
</style>
